import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Radio, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SectionHeading } from '@/components/sections/SectionHeading';
import { DeviceFrame, SignalsScreen, ProviderScreen } from '@/components/AppScreens';

const PATH_CONFIG = {
  subscribers: { to: '/followers', icon: Users, accent: '#17FDFD', Screen: SignalsScreen },
  providers: { to: '/providers', icon: Radio, accent: '#00FFA3', Screen: ProviderScreen },
};

const PathCard = ({ p, reverse }) => {
  const Icon = p.icon;
  const Screen = p.Screen;
  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        className="group relative grid overflow-hidden rounded-[2rem] border bg-[#001826]/70 backdrop-blur-xl transition-colors duration-500 lg:grid-cols-12"
        style={{ borderColor: `${p.accent}26` }}
      >
        <div
          className="pointer-events-none absolute -top-16 -z-0 h-72 w-72 rounded-full opacity-25 blur-[110px] transition-opacity duration-500 group-hover:opacity-50"
          style={{ background: p.accent, [reverse ? 'right' : 'left']: '-3rem' }}
        />

        <div className={`relative z-10 flex flex-col items-center justify-center p-8 text-center sm:p-12 lg:col-span-7 lg:items-start lg:text-left ${reverse ? 'lg:order-2' : ''}`}>
          <span className="text-[11px] font-semibold uppercase tracking-[0.28em]" style={{ color: p.accent }}>{p.kicker}</span>
          <div className="mt-3 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: `${p.accent}1a`, color: p.accent }}>
              <Icon className="h-4 w-4" />
            </span>
            <span className="text-sm font-medium text-white/60">“{p.tag}”</span>
          </div>
          <h3 className="mt-4 font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl">{p.title}</h3>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-white/60">{p.plain}</p>
          <ul className="mt-7 space-y-3 text-left">
            {p.points.map((pt) => (
              <li key={pt} className="flex items-center gap-3 text-[15px] text-white/85">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: `${p.accent}1f`, color: p.accent }}>
                  <Check className="h-3 w-3" />
                </span>
                {pt}
              </li>
            ))}
          </ul>
          <Link
            to={p.to}
            data-testid={`path-${p.id}`}
            className="group/btn mt-9 inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-black transition-all hover:-translate-y-0.5"
            style={{ background: p.accent, boxShadow: `0 0 24px ${p.accent}55` }}
          >
            {p.cta}
            <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
          </Link>
        </div>

        <div className={`relative flex items-center justify-center overflow-hidden px-6 py-12 lg:col-span-5 ${reverse ? 'lg:order-1' : ''}`}>
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: `radial-gradient(120% 70% at 50% 55%, ${p.accent}22, transparent 66%)` }}
          />
          <div className="grid-bg pointer-events-none absolute inset-0 opacity-30" />
          <div
            className="pointer-events-none absolute bottom-10 left-1/2 h-10 w-44 -translate-x-1/2 rounded-[100%] blur-2xl"
            style={{ background: `${p.accent}40` }}
          />
          <motion.div
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 120, damping: 18 }}
            className="perspective relative z-10"
          >
            <DeviceFrame width="w-[206px]" glow={false} tilt radius="sm">
              <Screen />
            </DeviceFrame>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export const PathChooser = () => {
  const { t } = useTranslation();
  const paths = t('pathChooser.paths', { returnObjects: true }).map((p) => ({ ...p, ...PATH_CONFIG[p.id] }));
  return (
    <section className="px-6 py-24 sm:py-32 lg:px-8">
      <SectionHeading
        eyebrow={t('pathChooser.eyebrow')}
        title={t('pathChooser.title')}
        subtitle={t('pathChooser.subtitle')}
      />
      <div className="mx-auto mt-16 max-w-6xl space-y-8">
        {paths.map((p, i) => (
          <PathCard key={p.id} p={p} reverse={i % 2 === 1} />
        ))}
      </div>
    </section>
  );
};
